// Serviço para gerenciar transportadoras no Supabase
import { supabase } from '../lib/supabase';
import type { Transportadora, Endereco } from '../types';

// Interface para transportadora no banco de dados
interface TransportadoraDB {
  id: string;
  nome: string;
  cnpj: string;
  email: string;
  telefone: string;
  endereco_id: string;
  conta_id: string;
  empresa_id: string;
  created_at: string;
  updated_at: string;
}

// Converter transportadora do banco para o formato da aplicação
function transportadoraDBToTransportadora(transportadoraDB: TransportadoraDB, endereco: Endereco): Transportadora {
  return {
    id: transportadoraDB.id,
    nome: transportadoraDB.nome,
    cnpj: transportadoraDB.cnpj,
    email: transportadoraDB.email,
    telefone: transportadoraDB.telefone,
    endereco,
    createdAt: transportadoraDB.created_at,
    updatedAt: transportadoraDB.updated_at,
  };
}

// Buscar todas as transportadoras da empresa atual
export async function getTransportadoras(empresaId: string): Promise<Transportadora[]> {
  try {
    // Buscar transportadoras sem join para evitar recursão RLS
    const { data: transportadoras, error: transportadorasError } = await supabase
      .from('transportadoras')
      .select('id, nome, cnpj, email, telefone, endereco_id, created_at, updated_at')
      .eq('empresa_id', empresaId)
      .order('created_at', { ascending: false });

    if (transportadorasError) {
      console.error('[transportadorasService] Erro ao buscar transportadoras:', transportadorasError);
      return [];
    }

    if (!transportadoras || transportadoras.length === 0) {
      return [];
    }

    // Buscar endereços separadamente para evitar recursão
    const enderecoIds = transportadoras.map(t => t.endereco_id).filter(Boolean);
    const enderecosMap = new Map<string, Endereco>();

    if (enderecoIds.length > 0) {
      const { data: enderecos, error: enderecosError } = await supabase
        .from('enderecos')
        .select('*')
        .in('id', enderecoIds);

      if (!enderecosError && enderecos) {
        enderecos.forEach((e: any) => {
          enderecosMap.set(e.id, {
            cep: e.cep || '',
            logradouro: e.logradouro || '',
            numero: e.numero || '',
            complemento: e.complemento || undefined,
            bairro: e.bairro || '',
            cidade: e.cidade || '',
            estado: e.estado || '',
          });
        });
      }
    }

    // Montar transportadoras com endereços
    return transportadoras.map((t: any) => {
      const endereco = enderecosMap.get(t.endereco_id) || {
        cep: '',
        logradouro: '',
        numero: '',
        bairro: '',
        cidade: '',
        estado: '',
      };

      return transportadoraDBToTransportadora({ ...t, endereco_id: t.endereco_id }, endereco);
    });
  } catch (error) {
    console.error('[transportadorasService] Erro geral ao buscar transportadoras:', error);
    return [];
  }
}

// Buscar transportadora por ID
export async function getTransportadoraById(id: string): Promise<Transportadora | null> {
  try {
    const { data: transportadora, error } = await supabase
      .from('transportadoras')
      .select('*, enderecos(*)')
      .eq('id', id)
      .single();

    if (error) throw error;
    if (!transportadora) return null;

    const endereco: Endereco = {
      cep: transportadora.enderecos?.cep || '',
      logradouro: transportadora.enderecos?.logradouro || '',
      numero: transportadora.enderecos?.numero || '',
      complemento: transportadora.enderecos?.complemento || undefined,
      bairro: transportadora.enderecos?.bairro || '',
      cidade: transportadora.enderecos?.cidade || '',
      estado: transportadora.enderecos?.estado || '',
    };

    return transportadoraDBToTransportadora(transportadora, endereco);
  } catch (error) {
    console.error('Erro ao buscar transportadora:', error);
    throw error;
  }
}

// Criar nova transportadora
export async function createTransportadora(
  transportadora: Omit<Transportadora, 'id' | 'createdAt' | 'updatedAt'>,
  contaId: string,
  empresaId: string
): Promise<Transportadora> {
  try {
    // Primeiro, criar o endereço
    const { data: endereco, error: enderecoError } = await supabase
      .from('enderecos')
      .insert({
        cep: transportadora.endereco.cep,
        logradouro: transportadora.endereco.logradouro,
        numero: transportadora.endereco.numero,
        complemento: transportadora.endereco.complemento || null,
        bairro: transportadora.endereco.bairro,
        cidade: transportadora.endereco.cidade,
        estado: transportadora.endereco.estado,
      })
      .select()
      .single();

    if (enderecoError) throw enderecoError;

    // Depois, criar a transportadora
    const { data: novaTransportadora, error: transportadoraError } = await supabase
      .from('transportadoras')
      .insert({
        nome: transportadora.nome,
        cnpj: transportadora.cnpj,
        email: transportadora.email,
        telefone: transportadora.telefone,
        endereco_id: endereco.id,
        conta_id: contaId,
        empresa_id: empresaId,
      })
      .select()
      .single();

    if (transportadoraError) throw transportadoraError;

    return transportadoraDBToTransportadora(novaTransportadora, transportadora.endereco);
  } catch (error) {
    console.error('Erro ao criar transportadora:', error);
    throw error;
  }
}

// Atualizar transportadora
export async function updateTransportadora(id: string, transportadora: Partial<Transportadora>): Promise<Transportadora> {
  try {
    // Buscar transportadora atual para obter endereco_id
    const { data: transportadoraAtual, error: buscaError } = await supabase
      .from('transportadoras')
      .select('endereco_id, enderecos(*)')
      .eq('id', id)
      .single();

    if (buscaError) throw buscaError;

    // Atualizar endereço se fornecido
    if (transportadora.endereco && transportadoraAtual.endereco_id) {
      const { error: enderecoError } = await supabase
        .from('enderecos')
        .update({
          cep: transportadora.endereco.cep,
          logradouro: transportadora.endereco.logradouro,
          numero: transportadora.endereco.numero,
          complemento: transportadora.endereco.complemento || null,
          bairro: transportadora.endereco.bairro,
          cidade: transportadora.endereco.cidade,
          estado: transportadora.endereco.estado,
        })
        .eq('id', transportadoraAtual.endereco_id);

      if (enderecoError) throw enderecoError;
    }

    // Atualizar transportadora
    const updateData: any = {};
    if (transportadora.nome !== undefined) updateData.nome = transportadora.nome;
    if (transportadora.cnpj !== undefined) updateData.cnpj = transportadora.cnpj;
    if (transportadora.email !== undefined) updateData.email = transportadora.email;
    if (transportadora.telefone !== undefined) updateData.telefone = transportadora.telefone;

    const { data: transportadoraAtualizada, error: transportadoraError } = await supabase
      .from('transportadoras')
      .update(updateData)
      .eq('id', id)
      .select('*, enderecos(*)')
      .single();

    if (transportadoraError) throw transportadoraError;

    const endereco: Endereco = {
      cep: transportadoraAtualizada.enderecos?.cep || '',
      logradouro: transportadoraAtualizada.enderecos?.logradouro || '',
      numero: transportadoraAtualizada.enderecos?.numero || '',
      complemento: transportadoraAtualizada.enderecos?.complemento || undefined,
      bairro: transportadoraAtualizada.enderecos?.bairro || '',
      cidade: transportadoraAtualizada.enderecos?.cidade || '',
      estado: transportadoraAtualizada.enderecos?.estado || '',
    };

    return transportadoraDBToTransportadora(transportadoraAtualizada, endereco);
  } catch (error) {
    console.error('Erro ao atualizar transportadora:', error);
    throw error;
  }
}

// Deletar transportadora
export async function deleteTransportadora(id: string): Promise<void> {
  try {
    // Buscar transportadora para obter endereco_id
    const { data: transportadora, error: buscaError } = await supabase
      .from('transportadoras')
      .select('endereco_id')
      .eq('id', id)
      .single();

    if (buscaError) throw buscaError;

    // Deletar transportadora
    const { error: transportadoraError } = await supabase
      .from('transportadoras')
      .delete()
      .eq('id', id);

    if (transportadoraError) throw transportadoraError;

    // Deletar endereço se não estiver sendo usado
    if (transportadora.endereco_id) {
      const { error: enderecoError } = await supabase
        .from('enderecos')
        .delete()
        .eq('id', transportadora.endereco_id);

      // Não lançar erro se o endereço estiver sendo usado por outros registros
      if (enderecoError && !enderecoError.message.includes('violates foreign key')) {
        console.warn('Erro ao deletar endereço:', enderecoError);
      }
    }
  } catch (error) {
    console.error('Erro ao deletar transportadora:', error);
    throw error;
  }
}

// Buscar transportadoras por termo (para busca global)
export async function searchTransportadoras(term: string, empresaId: string): Promise<Transportadora[]> {
  try {
    const termLower = term.toLowerCase();
    
    const { data: transportadoras, error } = await supabase
      .from('transportadoras')
      .select('*, enderecos(*)')
      .eq('empresa_id', empresaId)
      .or(`nome.ilike.%${termLower}%,cnpj.ilike.%${term}%,email.ilike.%${termLower}%`)
      .limit(20);

    if (error) throw error;

    return (transportadoras || []).map((t: any) => {
      const endereco: Endereco = {
        cep: t.enderecos?.cep || '',
        logradouro: t.enderecos?.logradouro || '',
        numero: t.enderecos?.numero || '',
        complemento: t.enderecos?.complemento || undefined,
        bairro: t.enderecos?.bairro || '',
        cidade: t.enderecos?.cidade || '',
        estado: t.enderecos?.estado || '',
      };

      return transportadoraDBToTransportadora(t, endereco);
    });
  } catch (error) {
    console.error('Erro ao buscar transportadoras:', error);
    throw error;
  }
}

